import type { AlertLocation, AlertPreferences, AppConfig, BeachConditions, LiveReaction, PushStatus, ReactionKind, WeatherAlert } from "../shared/types";
import { getApiJson, reactionSocketUrl } from './platform';

export async function fetchConditions(): Promise<BeachConditions & { degraded?: boolean; error?: string }> {
  return getApiJson(`/api/conditions?live=${Date.now()}`, true);
}

export async function fetchPushStatus(): Promise<{ push: PushStatus }> {
  return fetchAppConfig();
}

export async function fetchAppConfig(): Promise<AppConfig> {
  return getApiJson('/api/config');
}

export async function fetchAlerts(): Promise<{ alerts: WeatherAlert[]; degraded?: boolean; error?: string }> {
  return getApiJson('/api/alerts');
}

export async function savePushSubscription(subscription: PushSubscription, preferences: AlertPreferences, location?: AlertLocation): Promise<void> {
  const response = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ subscription, preferences, location })
  });
  if (!response.ok) throw new Error(`Push subscribe failed: ${response.status}`);
}

export type ReactionSocket = {
  sendReaction: (kind: ReactionKind) => boolean;
  close: () => void;
};

const REACTION_RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 5_000];
const REACTION_SEND_FLUSH_MS = 260;
const MAX_PENDING_REACTION_SENDS = 24;

export function connectReactions(options: {
  onReaction: (reaction: LiveReaction) => void;
  onHello: (reactions: LiveReaction[]) => void;
  onStatus: (message: string) => void;
}): ReactionSocket {
  const url = reactionSocketUrl();
  const pending: ReactionKind[] = [];
  let socket: WebSocket | null = null;
  let closed = false;
  let reconnectAttempt = 0;
  let reconnectTimer: number | undefined;
  let flushTimer: number | undefined;

  function openSocket() {
    if (closed) return;
    if (socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) return;

    const nextSocket = new WebSocket(url);
    socket = nextSocket;

    nextSocket.addEventListener("open", () => {
      if (socket !== nextSocket) return;
      reconnectAttempt = 0;
      options.onStatus("Live beach moments connected");
      flushPending();
    });
    nextSocket.addEventListener("close", () => {
      if (socket === nextSocket) socket = null;
      if (!closed) {
        options.onStatus("Beach moments reconnecting");
        scheduleReconnect();
      }
    });
    nextSocket.addEventListener("error", () => {
      if (!closed) options.onStatus("Beach moments reconnecting");
    });
    nextSocket.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(event.data as string) as
          | { type: "hello"; reactions: LiveReaction[] }
          | { type: "reaction"; reaction: LiveReaction }
          | { type: "notice"; message: string };
        if (message.type === "hello") options.onHello(message.reactions);
        if (message.type === "reaction") options.onReaction(message.reaction);
        if (message.type === "notice") options.onStatus(message.message);
      } catch {
        options.onStatus("Beach moments skipped one garbled update");
      }
    });
  }

  function scheduleReconnect() {
    if (closed || reconnectTimer !== undefined) return;
    const delay = REACTION_RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, REACTION_RECONNECT_DELAYS_MS.length - 1)];
    reconnectAttempt += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined;
      openSocket();
    }, delay);
  }

  function sendNow(kind: ReactionKind) {
    if (socket?.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify({ type: "reaction", kind }));
    return true;
  }

  function queueReaction(kind: ReactionKind) {
    if (pending.length >= MAX_PENDING_REACTION_SENDS) pending.shift();
    pending.push(kind);
    openSocket();
  }

  function flushPending() {
    if (closed || flushTimer !== undefined || !pending.length) return;
    const kind = pending.shift();
    if (!kind) return;
    if (!sendNow(kind)) {
      pending.unshift(kind);
      openSocket();
      return;
    }
    if (pending.length) {
      flushTimer = window.setTimeout(() => {
        flushTimer = undefined;
        flushPending();
      }, REACTION_SEND_FLUSH_MS);
    }
  }

  openSocket();

  return {
    sendReaction(kind) {
      if (sendNow(kind)) return true;
      queueReaction(kind);
      options.onStatus("Beach moment queued to share");
      return false;
    },
    close() {
      closed = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      if (flushTimer !== undefined) window.clearTimeout(flushTimer);
      socket?.close();
    }
  };
}

export function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
  return buffer;
}
